import type { Dispatch } from '@reduxjs/toolkit'
import type { NavigateFunction } from 'react-router-dom'
import type { AppContextType } from '../../../App'
import { buildChartAiMetadata } from '../../chartV2/aiChat/chartContext'
import { applyChartCommands, type ChartCommand } from '../../chartV2/aiChat/chartCommands'
import type { ChartCommandApplyResults } from '../../screener/aiChat/types'
import type { RootState } from '../../../store'
import type { Security } from '../../../proto/generated/security'
import type { ChartAlertSnapshot } from '../../alerts/alertModal.typings'
import type { SurfaceApplyResult } from '../surfaceRegistry'

export function buildChartSurfaceMetadata(
    state: RootState,
    chartId: string | null | undefined,
    getChart: AppContextType['getChart'],
): Record<string, unknown> {
    const chart = chartId ? getChart(chartId) : undefined
    return buildChartAiMetadata(state, chartId, chart)
}

export async function applyChartSurfaceArtifacts(
    metadata: Record<string, unknown>,
    deps: {
        viewKey: string
        getChart: AppContextType['getChart']
        dispatch: Dispatch
        setPrimarySecurity: (
            key: string,
            securities: Security[],
            expressionMode: boolean,
            displayName?: string,
        ) => void
        dashboard?: AppContextType['dashboard']
        navigate?: NavigateFunction
        getChartSnapshot?: () => ChartAlertSnapshot | null
    },
): Promise<SurfaceApplyResult | null> {
    const rawCommands = metadata.commands
    if (!Array.isArray(rawCommands) || !rawCommands.length) {
        return null
    }
    const commands = rawCommands as ChartCommand[]
    const viewKey = targetChartIdFromMetadata(metadata) ?? deps.viewKey
    try {
        const results: ChartCommandApplyResults = await applyChartCommands(commands, {
            viewKey,
            chart: deps.getChart(viewKey),
            dispatch: deps.dispatch,
            setPrimarySecurity: deps.setPrimarySecurity,
            dashboard: deps.dashboard,
            getChart: deps.getChart,
            navigate: deps.navigate,
            getChartSnapshot: deps.getChartSnapshot,
        })
        return results
    } catch {
        return {
            applied: [],
            failed: [{ op: 'apply', message: 'Some chart changes could not be applied.' }],
        }
    }
}

export function chartCommandsFromMetadata(metadata: Record<string, unknown>): ChartCommand[] {
    const raw = metadata.commands
    if (!Array.isArray(raw)) return []
    return raw as ChartCommand[]
}

export function chartCommandsFromNewMessages(metadata: Record<string, unknown>): ChartCommand[] {
    const raw = metadata.new_messages
    if (!Array.isArray(raw)) return []
    const commands: ChartCommand[] = []
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object') continue
        const msg = entry as Record<string, unknown>
        if (msg.kind !== 'custom' || msg.type !== 'chart_command') continue
        const data = msg.data as Record<string, unknown> | undefined
        const cmdList = data?.commands
        if (Array.isArray(cmdList)) {
            commands.push(...(cmdList as ChartCommand[]))
        }
    }
    return commands
}

export function targetChartIdFromMetadata(metadata: Record<string, unknown>): string | undefined {
    const target = metadata.target
    if (target && typeof target === 'object') {
        const chartId = (target as Record<string, unknown>).chart_id
        if (typeof chartId === 'string' && chartId.trim()) return chartId
    }
    const raw = metadata.new_messages
    if (!Array.isArray(raw)) return undefined
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object') continue
        const msg = entry as Record<string, unknown>
        if (msg.kind !== 'custom' || msg.type !== 'chart_command') continue
        const data = msg.data as Record<string, unknown> | undefined
        const nested = data?.target
        if (nested && typeof nested === 'object') {
            const chartId = (nested as Record<string, unknown>).chart_id
            if (typeof chartId === 'string' && chartId.trim()) return chartId
        }
    }
    return undefined
}
